/**
 * SmartChatBubble — public helper widget on the landing page.
 *
 * "Mark" is a rule-based assistant. It only answers from a built-in FAQ
 * knowledge base (no live LLM calls, no web search) so the answers are
 * always accurate, always free, and always fast.
 *
 * The UX mirrors the ResumeNow-style helper the user asked for:
 *   - A friendly greeting card
 *   - 4 topic buttons (For tenants · For landlords · Pricing · Contact support)
 *   - Each topic reveals sub-topic Q&A cards
 *   - A free-text input matches keywords against the FAQ; if there is no
 *     match it politely routes the visitor back to the topic buttons.
 *
 * The demo scheduler + email lead capture flows are kept because they
 * are still useful (visitors who want a walk-through, not just answers).
 */
import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Loader2, Sparkles, Calendar, ArrowLeft, CheckCircle2, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { getAttribution, newEventId, firePixelLead } from "@/lib/attribution";
import { useBotShield, BotHoneypot } from "@/lib/botShield";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const SESSION_KEY = "cr_chat_session";

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

// ─────────────────────────── Built-in knowledge base ───────────────────────────
// Each topic has:
//   id        — string identifier used for testids and keys
//   label     — button label
//   intro     — one-line summary shown when the topic is opened
//   items     — array of { q, a, to?, keywords[] }
//     q        — sub-topic button label / question
//     a        — the plain-English answer
//     to       — optional in-app link Mark can point the visitor to
//     keywords — words used to match free-text input to this Q
const TOPICS = [
  {
    id: "tenants",
    label: "For tenants",
    intro: "Here's what tenants can do in Central Rentals.",
    items: [
      {
        q: "How do I pay rent?",
        a: "You pay rent inside your tenant portal in Canadian dollars. Choose card, Interac, or pre-authorized debit — you'll get an instant receipt every time.",
        to: "/signup?role=tenant",
        keywords: ["pay", "rent", "payment", "interac", "card", "debit"],
      },
      {
        q: "How do I ask for a repair?",
        a: "Open your tenant portal, tap Maintenance, add photos and a short description. Your landlord sees the ticket instantly and you can chat right there.",
        to: "/signup?role=tenant",
        keywords: ["repair", "maintenance", "broken", "fix", "ticket"],
      },
      {
        q: "Where do I find my lease?",
        a: "Every signed lease and addendum is stored under Documents in your portal — you can download it any time.",
        keywords: ["lease", "document", "contract", "agreement"],
      },
      {
        q: "Can I build credit by paying rent?",
        a: "Yes. When you opt in, your on-time rent payments are reported so they help build your credit history.",
        to: "/tenant-credit",
        keywords: ["credit", "score", "history", "build"],
      },
      {
        q: "What are my rights in my province?",
        a: "We have plain-English guides for all 13 provinces and territories — deposits, notices, evictions, and repairs.",
        to: "/answers",
        keywords: ["rights", "law", "province", "eviction", "deposit", "notice"],
      },
      {
        q: "Can I split rent with roommates?",
        a: "Yes. Each roommate can log in with their own account and pay their share separately. Everyone gets their own receipt for tax time.",
        to: "/signup?role=tenant",
        keywords: ["roommate", "split", "share", "co-tenant", "partner"],
      },
      {
        q: "How do I get my deposit back?",
        a: "Once you move out and the inspection is done, your landlord releases the deposit through the portal. Rules and timelines vary by province — check the guide for yours.",
        to: "/answers",
        keywords: ["deposit", "security", "back", "return", "refund", "move out"],
      },
      {
        q: "Is my personal info safe?",
        a: "Yes. We're PIPEDA-compliant, use TLS 1.3 for every request, and never sell your data. Payment cards are stored by Stripe, never on our servers.",
        keywords: ["safe", "privacy", "secure", "pipeda", "data", "gdpr"],
      },
    ],
  },
  {
    id: "landlords",
    label: "For landlords",
    intro: "Everything landlords can do inside Central Rentals.",
    items: [
      {
        q: "What can I do with Central Rentals?",
        a: "Collect rent, generate Canadian leases, screen tenants, track maintenance, store documents, and export tax-ready reports — all in one dashboard.",
        to: "/signup?role=landlord",
        keywords: ["what", "features", "do", "help", "manage"],
      },
      {
        q: "Do you support my province?",
        a: "Yes — we cover all 13 provinces and territories, including Ontario Standard Lease and every N1–N13 notice form.",
        to: "/landlord-laws",
        keywords: ["province", "ontario", "bc", "alberta", "quebec", "notice", "n4", "n1"],
      },
      {
        q: "How does tenant screening work?",
        a: "Applicants fill out a digital application. You run a credit and background check through Certn (PIPEDA-compliant). Results appear in your dashboard so you can approve or decline.",
        to: "/features/tenant-screening-canada",
        keywords: ["screening", "credit", "check", "background", "applicant"],
      },
      {
        q: "Can I export my tax reports?",
        a: "Yes. One click exports T776, NR4, and T5013 reports ready to hand to your accountant.",
        to: "/features/t776-tax-export-software",
        keywords: ["tax", "t776", "nr4", "cra", "accountant", "report"],
      },
      {
        q: "How do I create a lease?",
        a: "Use the lease builder — pick your province, fill in the blanks, add custom clauses, and send it for e-signature. Signed copies save automatically.",
        to: "/features/provincial-lease-generator",
        keywords: ["lease", "generate", "create", "sign", "e-sign", "template"],
      },
      {
        q: "How do I get started?",
        a: "Create your landlord account (about a minute), add your first property, invite your tenants, and start collecting rent the same day.",
        to: "/signup?role=landlord",
        keywords: ["start", "sign up", "begin", "onboard", "first"],
      },
      {
        q: "Can I import my existing data?",
        a: "Yes — CSV import for properties, tenants, and past payments. If you're switching from Buildium, TenantCloud, or a spreadsheet, our migration wizard walks you through it.",
        to: "/migration",
        keywords: ["import", "csv", "migrate", "switch", "buildium", "tenantcloud", "spreadsheet", "excel"],
      },
      {
        q: "What integrations do you support?",
        a: "Stripe for payments, Certn for screening, QuickBooks for books, DocuSign-style e-signature, Kijiji + Facebook for listings, and Zapier for everything else.",
        to: "/integrate",
        keywords: ["integrations", "integrate", "stripe", "quickbooks", "kijiji", "zapier", "docusign"],
      },
      {
        q: "How do late fees work?",
        a: "Set a rule (e.g. 5% after 5 days) and the system applies it automatically. Reminders go out by email and SMS, and the tenant sees the fee in their portal.",
        keywords: ["late", "fee", "penalty", "overdue", "nsf"],
      },
      {
        q: "Can my property manager or bookkeeper have their own login?",
        a: "Yes. Invite your team from Settings → Team. You control who sees what — full-access owner, property manager, bookkeeper, or view-only accountant.",
        to: "/landlord-team",
        keywords: ["team", "manager", "bookkeeper", "accountant", "invite", "user", "role", "permission", "staff"],
      },
      {
        q: "Does it work on mobile?",
        a: "Yes — the app is mobile-first and works on any phone browser. Approve maintenance, chat with tenants, and check rent status from the palm of your hand.",
        keywords: ["mobile", "phone", "ios", "android", "app"],
      },
    ],
  },
  {
    id: "pricing",
    label: "Pricing",
    intro: "Straightforward monthly plans — no setup fees, cancel any time.",
    items: [
      {
        q: "How much does it cost?",
        a: "Starter is $35/month for 1–10 units. Growth is $50/month for 20–100 units. Enterprise is $80/month for 100+ units.",
        to: "/pricing",
        keywords: ["cost", "price", "how much", "plan", "fee", "monthly"],
      },
      {
        q: "Is there a paid plan?",
        a: "You can create an account for free and browse the dashboard. To start collecting rent and send leases you'll need to pick a plan.",
        to: "/pricing",
        keywords: ["free", "trial", "try", "demo"],
      },
      {
        q: "What's in the Starter plan?",
        a: "Rent collection, lease uploads and e-sign, listings, expense tracking, tenant applications, and maintenance tickets.",
        to: "/pricing",
        keywords: ["starter", "basic", "cheap"],
      },
      {
        q: "What's in the Growth plan?",
        a: "Everything in Starter plus unlimited e-signatures, automatic late-fee handling, richer reporting, and move-out risk alerts.",
        to: "/pricing",
        keywords: ["growth", "professional", "scale"],
      },
      {
        q: "Can I cancel any time?",
        a: "Yes. Cancel from Settings → Billing whenever you want — no lock-in, no cancellation fees.",
        keywords: ["cancel", "refund", "lock", "commit"],
      },
      {
        q: "Do tenants pay any fees?",
        a: "Tenants pay the standard Stripe payment fee only if the landlord chooses to pass it on — otherwise no fees. Central Rentals never charges tenants directly.",
        keywords: ["tenant fee", "renter", "hidden", "charge", "stripe fee", "processing"],
      },
      {
        q: "Are there any setup or hidden fees?",
        a: "No — one flat monthly price. No setup, no per-unit charges, no per-lease charges, no surprise fees.",
        keywords: ["setup", "hidden", "extra", "per unit", "per lease", "surcharge"],
      },
      {
        q: "Do you offer discounts?",
        a: "Yes — pay yearly and get 2 months free. Non-profit and Indigenous-led housing providers get 50% off. Contact support to apply.",
        keywords: ["discount", "annual", "yearly", "non-profit", "student", "coupon", "promo"],
      },
    ],
  },
  {
    id: "support",
    label: "Contact support",
    intro: "We reply within one business day.",
    items: [
      {
        q: "Email us",
        a: "Send us a note at contact@centralrentals.ca — we reply within one business day.",
        keywords: ["email", "hello", "contact", "reach"],
      },
      {
        q: "Open a support ticket",
        a: "Use the help form on the homepage or your dashboard. Your ticket goes straight to our team and you get an email confirmation.",
        to: "/#help-centre",
        keywords: ["ticket", "support", "help", "form", "issue"],
      },
      {
        q: "Book a 15-minute demo",
        a: "Pick a time that works for you and we'll walk you through the product live.",
        keywords: ["demo", "walk", "call", "book", "meeting"],
        actionBook: true,
      },
      {
        q: "Business hours",
        a: "We're open Monday to Friday, 9am–6pm ET. Weekends we're closed but we'll reply first thing Monday.",
        keywords: ["hours", "open", "weekend", "monday"],
      },
      {
        q: "I forgot my password",
        a: "On the sign-in page, click 'Forgot password' — we'll email you a reset link that's good for 30 minutes.",
        to: "/forgot",
        keywords: ["password", "forgot", "reset", "login", "cannot sign in"],
      },
      {
        q: "Is there a phone number?",
        a: "Yes — call 780-405-2498 during business hours. If we miss you, leave a voicemail and we'll call back within one business day.",
        keywords: ["phone", "call", "voicemail", "number"],
      },
    ],
  },
];

// Match free text against the FAQ knowledge base. Simple keyword scoring.
function matchFAQ(text) {
  const t = text.toLowerCase();
  let best = null;
  let bestScore = 0;
  for (const topic of TOPICS) {
    for (const item of topic.items) {
      let score = 0;
      for (const kw of item.keywords || []) {
        if (t.includes(kw)) score += kw.length; // longer keyword = stronger match
      }
      if (score > bestScore) {
        bestScore = score;
        best = { topic, item };
      }
    }
  }
  return bestScore >= 3 ? best : null;
}

export default function SmartChatBubble() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("home"); // "home" | "topic" | "schedule" | "confirmed"
  const [activeTopic, setActiveTopic] = useState(null);
  const [msgs, setMsgs] = useState([]); // { role, text, to? }
  // Track which FAQ items have been picked per topic so we can hide
  // them from the sub-topic buttons after they've been answered.
  const [pickedByTopic, setPickedByTopic] = useState({}); // { topicId: Set<questionIdx> }
  const [input, setInput] = useState("");
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem("crc_mark_bubble_hidden") === "1"; } catch { return false; }
  });
  const dismiss = (e) => {
    e.stopPropagation();
    e.preventDefault();
    try { localStorage.setItem("crc_mark_bubble_hidden", "1"); } catch {}
    setHidden(true);
  };
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open, view]);

  // Reset to home whenever the bubble closes so reopening always lands on
  // the friendly greeting card.
  useEffect(() => {
    if (!open) {
      setView("home");
      setActiveTopic(null);
      setMsgs([]);
      setPickedByTopic({});
      setBookingResult(null);
    }
  }, [open]);

  function openTopic(topic) {
    setActiveTopic(topic);
    setView("topic");
  }

  function pickItem(topic, item) {
    setActiveTopic(topic);
    setView("topic");
    // Mark this Q as picked so it disappears from the sub-topic list.
    const idx = topic.items.indexOf(item);
    if (idx !== -1) {
      setPickedByTopic((prev) => {
        const next = { ...prev };
        const set = new Set(next[topic.id] || []);
        set.add(idx);
        next[topic.id] = set;
        return next;
      });
    }
    setMsgs((m) => [
      ...m,
      { role: "user", text: item.q },
      { role: "assistant", text: item.a, to: item.to, actionBook: item.actionBook },
    ]);
  }

  function handleFreeText() {
    const text = input.trim();
    if (!text) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text }]);
    const match = matchFAQ(text);
    if (match) {
      setActiveTopic(match.topic);
      setView("topic");
      // Free-text matches also count as "picked" so they hide.
      const idx = match.topic.items.indexOf(match.item);
      if (idx !== -1) {
        setPickedByTopic((prev) => {
          const next = { ...prev };
          const set = new Set(next[match.topic.id] || []);
          set.add(idx);
          next[match.topic.id] = set;
          return next;
        });
      }
      setMsgs((m) => [
        ...m,
        { role: "assistant", text: match.item.a, to: match.item.to, actionBook: match.item.actionBook },
      ]);
    } else {
      setMsgs((m) => [
        ...m,
        {
          role: "assistant",
          text: "Hi! I'm Mark — pick a topic below and I'll help right away, or ask me anything about rent laws, deposits, evictions, or your account. For anything I can't answer, email contact@centralrentals.ca and we'll reply within a business day.",
          showTopics: true,
        },
      ]);
    }
  }

  async function submitEmail() {
    if (!email.trim() || emailSent) return;
    const eventId = newEventId();
    firePixelLead(eventId);
    try {
      const attrib = getAttribution();
      // We still log the lead so the growth team can follow up, but we do
      // NOT send it through the LLM chat endpoint anymore.
      await fetch(`${API}/api/help-centre/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "Chat lead",
          email: email.trim(),
          category: "other",
          subject: "Chat helper — send walk-through",
          message: "Visitor requested a personalised walk-through via the Mark chat helper.",
          ...attrib,
          event_id: eventId,
        }),
      });
      setEmailSent(true);
      setMsgs((m) => [
        ...m,
        { role: "assistant", text: "Thanks — we'll send you a personalised walk-through within 1 business day." },
      ]);
      try {
        const { track } = await import("@/lib/analytics");
        track("lead_capture", { source: "smart_chat_bubble", cta_label: "chat-email-submit" });
      } catch { /* analytics best-effort */ }
    } catch { /* no-op */ }
  }

  return (
    <>
      {/* Bubble trigger — Pocketpills-style pill with sparkle badge */}
      {!open && !hidden && (
        <div className="fixed bottom-20 right-3 sm:bottom-6 sm:right-5 z-40 inline-flex items-center">
        <button
          data-testid="smart-chat-open"
          onClick={() => setOpen(true)}
          aria-label="Ask Mark"
          className="group inline-flex items-center gap-3 pl-4 pr-2 py-2 sm:pl-5 sm:pr-2 sm:py-2 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 hover:from-emerald-600 hover:to-emerald-800 text-white text-sm sm:text-base font-extrabold shadow-xl shadow-emerald-600/30 transition-all hover:-translate-y-0.5"
          style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <span className="hidden sm:inline">Ask Mark</span>
          <span className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-emerald-700 shadow-sm">
            <MessageCircle className="w-5 h-5"/>
            <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white animate-pulse"/>
          </span>
        </button>
        <button
          data-testid="smart-chat-dismiss"
          onClick={dismiss}
          aria-label="Hide Mark"
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-slate-700 border border-slate-300 shadow-md flex items-center justify-center hover:bg-slate-100 z-10"
        >
          <X className="w-3.5 h-3.5"/>
        </button>
        </div>
      )}

      {/* Panel — modern rounded card with gradient header */}
      {open && !hidden && (
        <div data-testid="smart-chat-panel" className="fixed bottom-20 right-3 sm:bottom-6 sm:right-5 z-40 w-[360px] max-w-[calc(100vw-1.5rem)] h-[600px] max-h-[calc(100vh-6rem)] bg-white rounded-3xl shadow-2xl shadow-slate-900/20 border border-emerald-100 flex flex-col overflow-hidden">
          {/* Header — soft emerald gradient */}
          <div className="relative px-4 py-4 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white flex items-center justify-between overflow-hidden">
            <div className="pointer-events-none absolute -top-8 -right-8 w-32 h-32 rounded-full bg-emerald-400/25 blur-xl"/>
            <div className="relative flex items-center gap-3">
              {(view === "topic" || view === "schedule" || view === "confirmed") && (
                <button
                  onClick={() => { setView("home"); setActiveTopic(null); }}
                  data-testid="smart-chat-back"
                  className="p-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
                  aria-label="Back"
                >
                  <ArrowLeft className="w-4 h-4 text-white"/>
                </button>
              )}
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-inner">
                <Sparkles className="w-5 h-5 text-emerald-600"/>
              </div>
              <div>
                <div className="font-display font-extrabold text-base leading-tight">
                  {view === "schedule" ? "Book a 15-min demo" : view === "confirmed" ? "You're booked!" : "Mark"}
                </div>
                <div className="text-xs text-emerald-100 leading-tight flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse inline-block"/>
                  {view === "topic" && activeTopic ? activeTopic.label : "Central Rentals helper"}
                </div>
              </div>
            </div>
            <button data-testid="smart-chat-close" onClick={() => setOpen(false)} className="relative p-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors">
              <X className="w-4 h-4 text-white"/>
            </button>
          </div>

          {/* HOME VIEW — greeting + topic buttons */}
          {view === "home" && (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 space-y-5">
                <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-base text-slate-700 leading-relaxed" data-testid="smart-chat-greeting">
                  <p className="font-display text-2xl font-extrabold text-slate-900 mb-2">Hi! <span className="inline-block">👋</span></p>
                  <p>I&apos;m Mark, your Central Rentals helper. Ask me about rent laws, deposits, evictions, pricing, or your account — or pick a topic below to get started.</p>
                </div>

                <div className="space-y-2" data-testid="smart-chat-topics">
                  {TOPICS.map((topic) => (
                    <button
                      key={topic.id}
                      data-testid={`smart-chat-topic-${topic.id}`}
                      onClick={() => openTopic(topic)}
                      className="w-full flex items-center justify-between px-4 py-4 rounded-xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50 text-left transition-all group"
                    >
                      <span className="font-bold text-slate-900 text-base">{topic.label}</span>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-600 transition-colors"/>
                    </button>
                  ))}
                </div>
              </div>

              {/* Input row */}
              <div className="px-3 py-3 border-t border-slate-200 bg-white flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleFreeText())}
                  placeholder="Ask a question…"
                  data-testid="smart-chat-input"
                  className="flex-1 px-3 py-2.5 text-base border border-slate-300 rounded-lg outline-none focus:border-emerald-500"
                />
                <button
                  onClick={handleFreeText}
                  disabled={!input.trim()}
                  data-testid="smart-chat-send"
                  className="p-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
                >
                  <Send className="w-5 h-5"/>
                </button>
              </div>
            </>
          )}

          {/* TOPIC VIEW — sub-topic buttons + conversation trail */}
          {view === "topic" && activeTopic && (
            <>
              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
                <p className="text-sm text-slate-600" data-testid="smart-chat-topic-intro">{activeTopic.intro}</p>

                {/* Conversation history */}
                {msgs.map((m, i) => (
                  <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"} data-testid={`smart-chat-msg-${i}`}>
                    <div className={
                      m.role === "user"
                        ? "bg-emerald-600 text-white px-3.5 py-2.5 rounded-2xl rounded-br-sm text-base max-w-[85%]"
                        : "bg-white border border-slate-200 text-slate-800 px-3.5 py-2.5 rounded-2xl rounded-bl-sm text-base max-w-[85%] whitespace-pre-wrap"
                    }>
                      <div>{m.text}</div>
                      {m.role === "assistant" && m.to && (
                        <Link
                          to={m.to}
                          data-testid={`smart-chat-msg-link-${i}`}
                          className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-emerald-700 hover:text-emerald-800 hover:underline"
                        >
                          Open page <ChevronRight className="w-4 h-4"/>
                        </Link>
                      )}
                      {m.role === "assistant" && m.actionBook && (
                        <button
                          onClick={() => setView("schedule")}
                          data-testid={`smart-chat-msg-book-${i}`}
                          className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-emerald-700 hover:text-emerald-800 hover:underline"
                        >
                          <Calendar className="w-4 h-4"/> Book a demo
                        </button>
                      )}
                    </div>
                  </div>
                ))}

                {/* Sub-topic quick picks — hidden after the visitor
                    picks any question so they only see one answer at
                    a time. They can type in the input to ask more or
                    tap Back to see the topic list again. */}
                {(pickedByTopic[activeTopic.id]?.size || 0) === 0 && (
                  <div className="pt-2 space-y-1.5" data-testid="smart-chat-subtopics">
                    {activeTopic.items.map((item, idx) => (
                      <button
                        key={idx}
                        data-testid={`smart-chat-item-${activeTopic.id}-${idx}`}
                        onClick={() => pickItem(activeTopic, item)}
                        className="w-full text-left px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50 text-base text-slate-800 transition-all"
                      >
                        {item.q}
                      </button>
                    ))}
                  </div>
                )}
                {(pickedByTopic[activeTopic.id]?.size || 0) > 0 && (
                  <div className="pt-2 flex flex-col gap-2" data-testid="smart-chat-post-answer">
                    <p className="text-sm text-slate-500 text-center">Ask another question below, or tap <span className="font-bold text-slate-700">Back</span> to see all topics.</p>
                    <button
                      type="button"
                      data-testid="smart-chat-show-more-questions"
                      onClick={() => setPickedByTopic((p) => ({ ...p, [activeTopic.id]: new Set() }))}
                      className="self-center text-sm font-bold text-emerald-700 hover:text-emerald-800 underline underline-offset-4"
                    >
                      Show other questions
                    </button>
                  </div>
                )}
              </div>

              {/* Optional email lead capture after 2+ replies */}
              {msgs.filter((m) => m.role === "user").length >= 2 && !emailSent && (
                <div className="px-4 py-2 bg-emerald-50 border-t border-emerald-100" data-testid="smart-chat-lead">
                  <label htmlFor="smart-chat-email-input" className="block text-xs uppercase tracking-wider font-bold text-emerald-800 mb-1">
                    Want a personal walk-through? Drop your email
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="smart-chat-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.ca"
                      data-testid="smart-chat-email-input"
                      className="flex-1 px-2.5 py-2 text-sm border border-emerald-200 rounded-lg outline-none bg-white"
                    />
                    <button
                      onClick={submitEmail}
                      data-testid="smart-chat-email-submit"
                      disabled={!email.trim()}
                      className="text-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-2 rounded-lg disabled:opacity-50"
                    >
                      Send
                    </button>
                  </div>
                </div>
              )}

              <div className="px-3 py-3 border-t border-slate-200 bg-white flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), handleFreeText())}
                  placeholder="Ask another question…"
                  data-testid="smart-chat-input"
                  className="flex-1 px-3 py-2.5 text-base border border-slate-300 rounded-lg outline-none focus:border-emerald-500"
                />
                <button
                  onClick={handleFreeText}
                  disabled={!input.trim()}
                  data-testid="smart-chat-send"
                  className="p-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50"
                >
                  <Send className="w-5 h-5"/>
                </button>
              </div>
            </>
          )}

          {view === "schedule" && (
            <ScheduleView
              defaultEmail={email}
              onBooked={(result) => { setBookingResult(result); setView("confirmed"); }}
            />
          )}

          {view === "confirmed" && bookingResult && (
            <ConfirmedView booking={bookingResult} onClose={() => setOpen(false)} />
          )}
        </div>
      )}
    </>
  );
}


// ─────────────────────────── ScheduleView ──────────────────────────
function ScheduleView({ defaultEmail, onBooked }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: defaultEmail || "",
    phone: "",
    role: "landlord",
    units: "",
    timeline: "1-3mo",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const shield = useBotShield();

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/smart-chat/slots?days=14`);
        const d = await r.json();
        setDays(d.days || []);
        if ((d.days || []).length > 0) setSelectedDay(d.days[0].date);
      } catch {
        setError("Couldn't load times — please try again in a moment.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const dayObj = days.find((d) => d.date === selectedDay);

  async function submit() {
    if (!form.name.trim() || !form.email.trim() || !selectedSlot) return;
    setSubmitting(true);
    setError("");
    try {
      const attrib = getAttribution();
      const eventId = newEventId();
      const r = await fetch(`${API}/api/smart-chat/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: getSessionId(),
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          role: form.role,
          units: form.units ? Number(form.units) : null,
          timeline: form.timeline,
          slot_iso: selectedSlot,
          kind: "demo",
          notes: form.notes.trim() || null,
          event_id: eventId,
          ...attrib,
          ...shield.values(),
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Booking failed");
      }
      const d = await r.json();
      try {
        const { fireLeadFormSubmit } = await import("@/lib/google-tag");
        fireLeadFormSubmit({ source: "smart_chat_book_demo", email: form.email.trim(), valueCad: 50, transactionId: d.booking_id });
      } catch { /* never block UX */ }
      try {
        const { track } = await import("@/lib/analytics");
        track("demo_booked", { source: "smart_chat_bubble", slot: selectedSlot });
      } catch { /* best effort */ }
      onBooked({ ...d, name: form.name, email: form.email, slot_iso: selectedSlot });
    } catch (e) {
      setError(e.message || "Couldn't book — please pick another slot.");
    } finally {
      setSubmitting(false);
    }
  }

  function fmtDayLabel(iso) {
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
  }
  function fmtTime(iso) {
    const t = iso.slice(11, 16);
    const [h, m] = t.split(":").map(Number);
    const hh = ((h + 11) % 12) + 1;
    const ampm = h < 12 ? "AM" : "PM";
    return `${hh}:${m.toString().padStart(2, "0")} ${ampm}`;
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50" data-testid="smart-chat-schedule">
      {loading ? (
        <div className="py-8 text-center text-slate-500 text-sm flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin"/> Loading times…
        </div>
      ) : days.length === 0 ? (
        <div className="py-8 text-center text-slate-500 text-sm">No times available — please email contact@centralrentals.ca.</div>
      ) : (
        <>
          <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">Pick a day (ET)</div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="smart-chat-days">
            {days.slice(0, 10).map((d) => (
              <button
                key={d.date}
                onClick={() => { setSelectedDay(d.date); setSelectedSlot(null); }}
                data-testid={`smart-chat-day-${d.date}`}
                className={`flex-none px-3 py-2 rounded-lg text-xs font-bold border ${selectedDay === d.date ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
              >
                {fmtDayLabel(d.date)}
                <div className="text-[10px] font-normal opacity-70">{d.slots.length} open</div>
              </button>
            ))}
          </div>

          <div className="mt-3 text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-2">Pick a time</div>
          <div className="grid grid-cols-3 gap-1.5" data-testid="smart-chat-slots">
            {(dayObj?.slots || []).map((s) => (
              <button
                key={s}
                onClick={() => setSelectedSlot(s)}
                data-testid={`smart-chat-slot-${s}`}
                className={`px-1 py-1.5 rounded-md text-xs font-semibold border ${selectedSlot === s ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-500"}`}
              >
                {fmtTime(s)}
              </button>
            ))}
          </div>

          {selectedSlot && (
            <div className="mt-4 space-y-2" data-testid="smart-chat-form">
              <BotHoneypot {...shield.fieldProps} />
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Your details</div>
              <input data-testid="smart-chat-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              <input data-testid="smart-chat-form-email" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              <input data-testid="smart-chat-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              <div className="grid grid-cols-2 gap-2">
                <select data-testid="smart-chat-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="px-2 py-1.5 text-xs border border-slate-300 rounded-lg bg-white">
                  <option value="landlord">Landlord</option>
                  <option value="tenant">Tenant</option>
                  <option value="investor">Investor</option>
                  <option value="prop_manager">Property manager</option>
                </select>
                <input data-testid="smart-chat-units" type="number" min={0} placeholder="# units" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} className="px-2 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              </div>
              <select data-testid="smart-chat-timeline" value={form.timeline} onChange={(e) => setForm({ ...form, timeline: e.target.value })} className="w-full px-2 py-1.5 text-xs border border-slate-300 rounded-lg bg-white">
                <option value="now">Ready to switch now</option>
                <option value="1-3mo">In the next 1–3 months</option>
                <option value="3-6mo">3–6 months out</option>
                <option value="exploring">Just exploring</option>
              </select>
              <textarea data-testid="smart-chat-notes" placeholder="Anything I should know? (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
              {error && <div className="text-xs text-rose-600">{error}</div>}
              <button onClick={submit} disabled={submitting || !form.name.trim() || !form.email.trim()} data-testid="smart-chat-submit" className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50">
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin"/> Booking…</> : <><Calendar className="w-4 h-4"/> Confirm my 15-min demo</>}
              </button>
              <p className="text-[10px] text-slate-500 text-center">Free · Cancel any time · We respect your time</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────── ConfirmedView ──────────────────────────
function ConfirmedView({ booking, onClose }) {
  const slot = booking.slot_iso || "";
  const date = slot.slice(0, 10);
  const time = slot.slice(11, 16);
  function pretty() {
    try {
      const d = new Date(slot);
      return d.toLocaleString("en-CA", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: "America/Toronto" }) + " ET";
    } catch {
      return `${date} · ${time} ET`;
    }
  }
  return (
    <div className="flex-1 overflow-y-auto px-5 py-6 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/50 text-center" data-testid="smart-chat-confirmed">
      <div className="mx-auto w-14 h-14 rounded-full bg-emerald-600 text-white flex items-center justify-center">
        <CheckCircle2 className="w-7 h-7"/>
      </div>
      <div className="mt-4 font-display text-lg font-extrabold text-slate-900">You&apos;re booked!</div>
      <div className="mt-1 text-sm text-slate-600">{pretty()}</div>
      <div className="mt-5 rounded-xl bg-white border border-slate-200 p-4 text-left text-xs text-slate-700">
        <div className="font-bold text-slate-900 mb-1">What happens next</div>
        <ul className="space-y-1">
          <li>· Confirmation sent to {booking.email}</li>
          <li>· We&apos;ll reach out 5 min before with a video link</li>
          <li>· Bring questions — we&apos;ll tailor the walk-through</li>
        </ul>
      </div>
      {booking.ics_url && (
        <a
          href={`${API}${booking.ics_url}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="smart-chat-add-to-calendar"
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold"
        >
          <Calendar className="w-3.5 h-3.5"/> Add to calendar (.ics)
        </a>
      )}
      <div>
        <button onClick={onClose} data-testid="smart-chat-confirmed-close" className="mt-4 text-xs font-bold text-slate-500 hover:text-slate-700 underline">
          Close
        </button>
      </div>
    </div>
  );
}
